import type { SecurityPolicyDraft } from './policy';
import type { SmsProtection } from './smsProtection';

export type SecurityResolution = 'safe' | 'mitigated' | 'falsePositive';
export type ProtectionKind = 'loginSpray' | 'verificationFlood' | 'apiFlood';
export const protectionActions = {
  loginSpray: 'blockIp',
  verificationFlood: 'pauseVerification',
  apiFlood: 'throttleApi',
} as const;
export type ProtectionAction = 'observe' | (typeof protectionActions)[ProtectionKind];
export interface AutoProtectionRule {
  kind: ProtectionKind;
  enabled: boolean;
  windowMinutes: number;
  threshold: number;
  durationMinutes: number;
  action: ProtectionAction;
  ipBan?: {
    strategy: 'fixed' | 'progressive';
    secondHours: number;
    subsequentHours: number;
    resetHours: number;
  };
}
export interface AccessIpRule {
  id: string;
  address: string;
  note: string;
  enabled: boolean;
  source: 'manual' | 'automatic';
  expiresAt: string | null;
  eventId?: string;
}
export interface SecurityActor {
  id: string;
  name: string;
}
export interface SecurityEvent {
  id: string;
  type:
    | 'failedLogins'
    | 'newDevice'
    | 'blockedIp'
    | 'accountCompromised'
    | 'autoProtection';
  account: string;
  ip: string;
  severity: 'warning' | 'critical';
  status: 'open' | 'resolved';
  createdAt: string;
  processedAt: string | null;
  note: string;
  resolution: SecurityResolution | null;
  processedBy: SecurityActor | null;
  handlingRecords: {
    resolution: SecurityResolution;
    actor: SecurityActor;
    note: string;
    at: string;
  }[];
  restriction?: {
    kind: ProtectionKind;
    action: ProtectionAction;
    target: string;
    count: number;
    threshold: number;
    windowMinutes: number;
    startedAt: string;
    expiresAt: string;
    status: 'active' | 'expired' | 'released';
    releasedAt: string | null;
    releasedBy: SecurityActor | null;
    releaseNote: string;
  };
}
export interface SecuritySession {
  id: string;
  account: string;
  device: string;
  ip: string;
  current: boolean;
  status: 'active' | 'revoked' | 'expired';
  createdAt: string;
  lastActiveAt: string;
  endedAt: string | null;
  note: string;
}

export interface VersionedPage<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  version: number;
}
export interface LoginPolicyValues {
  maxFailures: number;
  lockMinutes: number;
  humanVerification: {
    mode: 'off' | 'always' | 'afterFailures';
    failureThreshold: number;
  };
  security: { policyDraft: SecurityPolicyDraft };
}
export type LoginPolicy = LoginPolicyValues;
export interface ProtectionPolicyValues {
  autoProtection: AutoProtectionRule[];
  smsProtection: SmsProtection;
}
export type ProtectionPolicy = Pick<ProtectionPolicyValues, 'autoProtection'>;
export interface AccessPolicyResource {
  version: number;
  login: LoginPolicy;
  protection: ProtectionPolicy;
  smsProtection: SmsProtection;
}
export interface IpRulesResource {
  version: number;
  allowed: AccessIpRule[];
  denied: AccessIpRule[];
}
export type IpRuleValues = Pick<
  AccessIpRule,
  'address' | 'note' | 'enabled' | 'expiresAt'
>;
export interface UpdateLoginPolicyInput {
  expectedVersion: number;
  values: LoginPolicyValues;
}
export interface UpdateProtectionPolicyInput {
  expectedVersion: number;
  values: ProtectionPolicyValues;
}
export interface CreateIpRuleInput {
  expectedVersion: number;
  list: 'allowed' | 'denied';
  values: IpRuleValues;
}
export interface UpdateIpRuleInput extends CreateIpRuleInput {
  id: string;
}
export interface DeleteIpRuleInput {
  expectedVersion: number;
  list: 'allowed' | 'denied';
  id: string;
}
export interface ProcessSecurityEventInput {
  expectedVersion: number;
  resolution: SecurityResolution;
  note: string;
}
export interface ReleaseSecurityRestrictionInput {
  expectedVersion: number;
  note: string;
}
export interface TerminateSecuritySessionInput {
  expectedVersion: number;
  note: string;
}
export interface SecurityListFilters {
  page?: number;
  pageSize?: number;
  account?: string;
  dateFrom?: string;
  dateTo?: string;
}
export interface SecurityEventFilters extends SecurityListFilters {
  status?: SecurityEvent['status'];
  type?: SecurityEvent['type'];
  severity?: SecurityEvent['severity'];
  resolution?: SecurityResolution;
}
export interface SecuritySessionFilters extends SecurityListFilters {
  status?: SecuritySession['status'];
}
export interface IpRuleFilters {
  page?: number;
  pageSize?: number;
  list?: 'allowed' | 'denied';
  address?: string;
  enabled?: boolean;
  source?: AccessIpRule['source'];
}
